import type { ServerAuthPolicy } from "@t3tools/contracts";

import { t } from "~/i18n";
import type { AccountTeamResult } from "../auth/team";
import { platformApiBaseUrl } from "../auth/platform-api-origin";

/**
 * A team admin's management of their own team, over platform-api's
 * `/api/team*` routes. Every request carries the session cookie; every answer
 * is read here into a value the settings section renders as it is.
 */

export type TeamRole = "admin" | "member";

export interface TeamMember {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly role: TeamRole;
}

export interface TeamInvite {
  readonly id: string;
  /** `null` on an invite issued before invites named their recipient. */
  readonly email: string | null;
  readonly expiresAt: string;
}

export interface TeamOverview {
  readonly name: string;
  readonly seats: number;
  readonly seatsUsed: number;
  readonly members: ReadonlyArray<TeamMember>;
  readonly invites: ReadonlyArray<TeamInvite>;
}

/**
 * Why a request did not succeed. `refused` carries the gateway's reason;
 * `unavailable` is an outage (no answer, a 5xx, or an answer this client cannot
 * read), the one failure a retry can fix.
 */
export type TeamFailure =
  | { readonly kind: "refused"; readonly code: string }
  | { readonly kind: "unavailable" };

export type TeamResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: TeamFailure };

export type InviteDelivery = "sent" | "not_configured" | "failed";

/** An invite just created or sent again, with the one link that works for it. */
export interface SentInvite {
  readonly id: string;
  readonly link: string;
  readonly delivery: InviteDelivery;
  readonly email: string;
}

/**
 * Whether the settings offer the team section: on a team server, to the team's
 * admin. `/api/me` names the role as the team routes admit it, so the section
 * never offers what the routes refuse. `pending` until both the server and
 * `/api/me` have answered; an `/api/me` that failed hides the section.
 */
export function teamSectionAccess(
  policy: ServerAuthPolicy | null,
  account: AccountTeamResult | undefined,
): "pending" | "visible" | "hidden" {
  if (policy === null) return "pending";
  if (policy !== "team-server") return "hidden";
  if (account === undefined) return "pending";
  return account.status === "ok" && account.team?.role === "admin" ? "visible" : "hidden";
}

export const hasFreeSeat = (team: TeamOverview): boolean => team.seatsUsed < team.seats;

/** An admin removes members only: neither themself nor another admin. */
export const canRemoveMember = (member: TeamMember, selfId: string | null): boolean =>
  member.role !== "admin" && member.id !== selfId;

// ---------------------------------------------------------------- requests

export function fetchTeam(fetchImpl: typeof fetch = fetch): Promise<TeamResult<TeamOverview>> {
  return request(fetchImpl, "GET", "/api/team", undefined, readOverview);
}

export function inviteMember(
  input: { readonly email: string },
  fetchImpl: typeof fetch = fetch,
): Promise<TeamResult<SentInvite>> {
  return request(fetchImpl, "POST", "/api/team/invites", input, readSentInvite);
}

export function resendInvite(
  inviteId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TeamResult<SentInvite>> {
  return request(
    fetchImpl,
    "POST",
    `/api/team/invites/${encodeURIComponent(inviteId)}/resend`,
    undefined,
    readSentInvite,
  );
}

export function revokeInvite(
  inviteId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TeamResult<null>> {
  return request(
    fetchImpl,
    "POST",
    `/api/team/invites/${encodeURIComponent(inviteId)}/revoke`,
    undefined,
    () => null,
  );
}

export function removeMember(
  userId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TeamResult<null>> {
  return request(
    fetchImpl,
    "POST",
    `/api/team/members/${encodeURIComponent(userId)}/remove`,
    undefined,
    () => null,
  );
}

const UNAVAILABLE = { ok: false, failure: { kind: "unavailable" } } as const;

async function request<T>(
  fetchImpl: typeof fetch,
  method: "GET" | "POST",
  path: string,
  body: unknown,
  /** Reads a success body; `undefined` is a body this client cannot read. */
  read: (body: unknown) => T | undefined,
): Promise<TeamResult<T>> {
  let response: Response;
  try {
    response = await fetchImpl(`${platformApiBaseUrl}${path}`, {
      method,
      credentials: "include",
      cache: "no-store",
      headers: {
        accept: "application/json",
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    return UNAVAILABLE;
  }
  const json: unknown = await response.json().catch(() => undefined);
  if (response.status >= 500) return UNAVAILABLE;
  if (!response.ok) {
    return { ok: false, failure: { kind: "refused", code: refusalCode(response.status, json) } };
  }
  const value = read(json);
  return value === undefined ? UNAVAILABLE : { ok: true, value };
}

/**
 * The gateway's own `code`, or one named for the status when the body has
 * none (a refusal from a proxy in front of it, or a body schema mismatch).
 */
function refusalCode(status: number, body: unknown): string {
  if (isRecord(body) && typeof body.code === "string" && body.code.length > 0) return body.code;
  if (status === 401) return "UNAUTHENTICATED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  return "UNKNOWN";
}

// ---------------------------------------------------------------- answers

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const text = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;

function readMember(value: unknown): TeamMember | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  const email = text(value.email);
  if (id === null || email === null) return null;
  return {
    id,
    name: typeof value.name === "string" ? value.name : "",
    email,
    role: value.role === "admin" ? "admin" : "member",
  };
}

function readInvite(value: unknown): TeamInvite | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  const expiresAt = text(value.expiresAt);
  if (id === null || expiresAt === null) return null;
  return { id, email: text(value.email), expiresAt };
}

function readList<T>(value: unknown, read: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const items = value.map(read);
  return items.every((item) => item !== null) ? (items as T[]) : null;
}

function readOverview(body: unknown): TeamOverview | undefined {
  if (!isRecord(body) || !isRecord(body.organization)) return undefined;
  const seats = count(body.organization.seats);
  const seatsUsed = count(body.seatsUsed);
  const members = readList(body.members, readMember);
  const invites = readList(body.invites, readInvite);
  if (seats === null || seatsUsed === null || members === null || invites === null) {
    return undefined;
  }
  return {
    name: typeof body.organization.name === "string" ? body.organization.name : "",
    seats,
    seatsUsed,
    members,
    invites,
  };
}

const DELIVERIES: ReadonlySet<string> = new Set(["sent", "not_configured", "failed"]);

function readSentInvite(body: unknown): SentInvite | undefined {
  if (!isRecord(body)) return undefined;
  const id = text(body.id);
  const link = text(body.link);
  const email = text(body.email);
  const delivery = typeof body.delivery === "string" ? body.delivery : "";
  if (id === null || link === null || email === null || !DELIVERIES.has(delivery)) {
    return undefined;
  }
  return { id, link, email, delivery: delivery as InviteDelivery };
}

// ---------------------------------------------------------------- words

const REFUSAL_MESSAGES: Record<string, string> = {
  NO_SEATS: "common/everySeatIsTaken_lncwur",
  ALREADY_MEMBER: "common/thisPersonIsAlready_jsa8yq",
  ACCEPT_NOT_CONFIGURED: "common/thisPersonAlreadyHas_xqcg1v",
  INVALID_EMAIL: "common/enterAValidEmail_g00xac",
  INVITE_CONSUMED: "common/thisInviteHasAlready_8m17gy",
  INVITE_REVOKED: "common/thisInviteHasAlready_e22d47",
  INVITE_WITHOUT_EMAIL: "common/theInviteNamesNo_bayezk",
  CANNOT_REMOVE_SELF: "common/youCannotRemoveYourself_coo4vz",
  CANNOT_REMOVE_ADMIN: "common/onlyTheDovodAdministrator_mzqzhi",
  NOT_FOUND: "common/itIsNoLonger_1cb1xp",
  UNAUTHENTICATED: "common/yourSessionHasEnded_xzga1v",
  FORBIDDEN: "common/onlyTheOrganizationAdministrator_5y5rs4",
};

export function teamFailureMessage(failure: TeamFailure): string {
  if (failure.kind === "unavailable") return t("common/theOrganizationServiceIs_fztk53");
  return t(REFUSAL_MESSAGES[failure.code] ?? "common/couldNotCompleteThe_pas5lh");
}

export const noSeatsMessage = (): string => t("common/everySeatIsTaken_lncwur");

export const seatsSummary = (team: TeamOverview): string =>
  t("common/takenOf_zcxa4r", { used: team.seatsUsed, seats: team.seats });

export const teamRoleLabel = (role: TeamRole): string =>
  role === "admin"
    ? t("common/organizationAdministrator_c5urhw")
    : t("common/organizationMember_asggfz");

export function inviteValidUntil(invite: TeamInvite, locale: string): string {
  const date = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" }).format(
    new Date(invite.expiresAt),
  );
  return t("common/validUntil_x9noje", { date });
}

/**
 * What the admin learns after an invite is sent. Mail that is not configured
 * and mail that failed both hand the admin the link, and only the failure is
 * worth sending again.
 */
export interface DeliveryNotice {
  readonly tone: "success" | "info" | "warning";
  readonly message: string;
  /** Shown for the admin to pass on when no email reached the person. */
  readonly link: string | null;
  readonly canResend: boolean;
}

export function deliveryNotice(invite: SentInvite): DeliveryNotice {
  const { email } = invite;
  switch (invite.delivery) {
    case "sent":
      return {
        tone: "success",
        message: t("common/emailSentTo_hd4vlh", { email }),
        link: null,
        canResend: false,
      };
    case "not_configured":
      return {
        tone: "info",
        message: t("common/emailIsNotConnected_9w4oyc", { email }),
        link: invite.link,
        canResend: false,
      };
    case "failed":
      return {
        tone: "warning",
        message: t("common/theEmailToWas_tx4co4", { email }),
        link: invite.link,
        canResend: true,
      };
  }
}
